import { useEffect, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { de } from 'date-fns/locale';
import Popup from "components/Popup/popup";
import commonStyles from "components/commonStyle/commonStyle.module.css";
import styles from "./Popup.module.css";
import "react-datepicker/dist/react-datepicker.css";
import { BookingFormValues, BookingPopupProps } from "modules/booking-popup";
import { WorkTimeSlot, HolidaySlot } from "modules/calendar-worktime-holiday";
import { fetchHolidaySlots, fetchWorkSlots } from "services/workTime/calendar-service";
import { getParentKids } from "services/get-parents-kids";
import { GoInfo } from "react-icons/go";
import { addWeeks, customSelectStyles, formatDate, getSlotsInDate } from "utils/bookingPopup";
import ErrorFailed from "components/errorFailed/errorFailed";
import { setBooking } from "services/booking/set-booking";
import { KidsData } from "modules/profile";
import SuccessPopup from "components/successPopup/successPopup";
import Select from 'react-select';
import { getCost } from "services/cost/get-cost";

registerLocale('de', de);

export default function CustomerBookingPopup({ isOpen, onClose }: BookingPopupProps) {
  const [formValues, setFormValues] = useState<BookingFormValues>({
    kid: "",
    block: [],
    recurrence: "",
    category: "",
    note: "",
    recurrenceDates: [null, null, null, null],
  });

  const [workTimeSlots, setWorkTimeSlots] = useState<WorkTimeSlot[]>([]);
  const [kidsData, setKidsData] = useState<KidsData[]>([]);
  const [holidaySlots, setHolidaySlots] = useState<HolidaySlot[]>([]);
  const [filteredBlocks, setFilteredBlocks] = useState<WorkTimeSlot[]>([]);
  const [costData, setCostData] = useState<{ id: string; category: string; price: number; }[]>([]);

  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [isPriceOpen, setIsPriceOpen] = useState(false);

  const currentDate = new Date();
  currentDate.setDate(currentDate.getDate() + 1);

  const minDate = currentDate;

  const selectedKid = kidsData.find(kid => kid.id.toString() == formValues.kid);
  const [error, setError] = useState<string | null>(null);
  const [isSuccessPopupOpen, setIsSuccessPopupOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const handleInfoOpen = () => setIsInfoOpen(true);
  const handleInfoClose = () => setIsInfoOpen(false);
  const handlePriceOpen = () => setIsPriceOpen(true);
  const handlePriceClose = () => setIsPriceOpen(false);

  const featchsData = async () => {
    try {
      const workData = await fetchWorkSlots();
      const costs = await getCost();
      const holidayData = await fetchHolidaySlots();
      const kidsData = await getParentKids();
      const filteredKids = (kidsData.kids || []).filter((kid: KidsData) => !kid.isArchived);
      setKidsData(filteredKids)
      setWorkTimeSlots(workData);
      setHolidaySlots(holidayData);
      setCostData(costs);
    } catch (error) {
      console.error("Fehler beim Abrufen der daten:", error);
    }
  };

  useEffect(() => {
    featchsData();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    setFormValues((prevValues) => {
      const updatedValues = {
        ...prevValues,
        [name]: value,
        age: selectedKid ? selectedKid.age : 0,
      };

      if (name === 'recurrence' && value === 'serial' && prevValues.bookingDate) {
        const recurrenceDates: (Date | null)[] = [];
        for (let i = 0; i < 4; i++) {
          const nextDate = addWeeks(new Date(prevValues.bookingDate), i );
          recurrenceDates.push(nextDate);
        }
        updatedValues.recurrenceDates = recurrenceDates;
      } else if (name === 'recurrence' && value !== 'serial') {
        updatedValues.recurrenceDates = [null, null, null, null];
      }

      return updatedValues;
    });
  };

  const handleClose = () => {
    setFormValues({
      kid: "",
      block: [],
      recurrence: "",
      category: "",
      note: "",
      recurrenceDates: [null, null, null, null],
    });
    onClose();
    setError(null);
    featchsData();
    setFilteredBlocks([]);
  };

  const handleDateChange = (date: Date | null) => {
    formValues.block = [];
    if (date) {
      setFormValues((prevValues) => {
        const updatedValues = { ...prevValues, bookingDate: date, block: [] };
        if (updatedValues.recurrence === 'serial') {
          const recurrenceDates: any[] = [];
          for (let i = 0; i < 4; i++) {
            const nextDate = addWeeks(new Date(date), i);
            recurrenceDates.push(nextDate);
          }
          updatedValues.recurrenceDates = recurrenceDates;
        }

        return updatedValues;
      });

      setFilteredBlocks(getSlotsInDate(date, workTimeSlots, holidaySlots));
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const slot = workTimeSlots.find((slot) =>
      formValues.block.includes(slot.id?.toString() ?? '')
    );
    if (!slot) {
      setError("Bitte wählen Sie einen gültigen Block.");
      setIsLoading(false);
      return;
    }

    const [startHour, startMinute] = slot.start_time
      ? slot.start_time.split(":").map(Number)
      : [0, 0];

    const startTime = formValues.bookingDate ? new Date(formValues.bookingDate) : new Date();
    startTime.setHours(startHour, startMinute, 0);

    if (startTime < currentDate) {
      setError("Bitte wählen Sie einen Termin, der mindestens 24 Stunden in der Zukunft liegt.");
      setIsLoading(false);
      return;
    }

    try {
      const response = await setBooking(formValues);
      if (!response.success) {
        setError(response.message || 'Es ist ein Fehler bei der Buchung aufgetreten. Bitte versuchen Sie es erneut.');
      } else {
        setIsSuccessPopupOpen(true);
      }
    } catch (error) {
      console.error("Fehler bei der Buchungsanfrage:", error);
      setError('Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.');
    } finally {
      setIsLoading(false);
    }
  };


  if (!isOpen) return null;
  return (
    <>
      <Popup
        title={"Neue Buchungsanfrage"}
        submitLabel={""}
        closeLabel={""}
        isWithButton={false}
        isOpen={true}
        onClose={handleClose}
      >
        <form onSubmit={handleSubmit}>
          {error && <ErrorFailed error={error} />}
          <div className={styles.form}>
            <div className={commonStyles.formGroup}>
              <label htmlFor="bookingDate">Datum*</label>
              <DatePicker
                selected={formValues.bookingDate}
                onChange={handleDateChange}
                dateFormat="EEEE, dd. MMMM yyyy"
                minDate={minDate}
                placeholderText="Wählen Sie ein Datum"
                wrapperClassName={commonStyles.datepickerWrapperOverride}
                id="bookingDate"
                name="bookingDate"
                locale="de"
              />
            </div>
            <div className={commonStyles.formGroupRow}>
              <div className={commonStyles.formGroup}>
                <label htmlFor="kid">Kind*</label>
                <select
                  id="kid"
                  name="kid"
                  value={formValues.kid}
                  onChange={handleInputChange}
                  required
                >
                  <option value="" disabled>
                    auswählen ...
                  </option>
                  {kidsData.map((kid, index) => (
                    <option key={index} value={kid.id.toString()}>
                      {kid.first_name} {kid.last_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className={commonStyles.formGroupOld}>
                <p>{selectedKid ? `${selectedKid.age} Jahre` : "0 Jahre"}</p>
              </div>
            </div>
            <div className={commonStyles.formGroupRow}>
              <div className={commonStyles.formGroup}>
                <label htmlFor="block">Blöcke* <GoInfo onClick={handlePriceOpen} /></label>
                <Select
                  id="block"
                  name="block"
                  isMulti
                  options={filteredBlocks
                    .filter((slot) => slot.id !== undefined)
                    .map((slot) => ({
                      value: slot.id!.toString(),
                      label: `${slot.start_time.substring(0, 5)} - ${slot.end_time.substring(0, 5)}${slot.number_of_places === 0 ? ' (ausgebucht)' : ''}`,
                      isDisabled: slot.number_of_places === 0,
                    }))}
                  value={formValues.block.map((blockId) => ({
                    value: blockId,
                    label: filteredBlocks.find((slot) => slot.id?.toString() === blockId)
                      ? `${filteredBlocks.find((slot) => slot.id?.toString() === blockId)?.start_time.substring(0, 5)} - ${filteredBlocks.find((slot) => slot.id?.toString() === blockId)?.end_time.substring(0, 5)}`
                      : '',
                  }))}
                  onChange={(selectedOptions) => {
                    setFormValues((prevValues) => ({
                      ...prevValues,
                      block: selectedOptions ? selectedOptions.map((option) => option.value) : [],
                    }));
                  }}
                  placeholder="Wählen Sie Blöcke aus ..."
                  styles={customSelectStyles}
                  required
                />
              </div>
              <div className={commonStyles.formGroup}>
                <label htmlFor="recurrence">
                  Wiederholung* <GoInfo onClick={handleInfoOpen} />
                </label>
                <select
                  id="recurrence"
                  name="recurrence"
                  value={formValues.recurrence}
                  onChange={(e) => {
                    handleInputChange(e);
                    if (e.target.value === "serial") {
                      if (!formValues.bookingDate) {
                        return;
                      }
                      const recurrenceDates: any[] = [];

                      for (let i = 0; i < 4; i++) {
                        const nextDate = addWeeks(new Date(formValues.bookingDate), i);
                        recurrenceDates.push(nextDate);
                      }
                      setFormValues((prevValues) => ({
                        ...prevValues,
                        recurrenceDates,
                      }));
                    } else {
                      setFormValues((prevValues) => ({
                        ...prevValues,
                        recurrenceDates: [null, null, null, null],
                      }));
                    }
                  }}
                  required
                >

                  <option value="" disabled>
                    auswählen ...
                  </option>
                  <option value="onetime">Einmalig</option>
                  <option value="serial">Wöchentlich</option>
                </select>
              </div>
            </div>
            {formValues.recurrence === "serial" && formValues.bookingDate && (
              <div className={commonStyles.formGroup}>
                <label>Enddatum: {formatDate(formValues.recurrenceDates[2])}</label>
              </div>
            )}

            <div className={commonStyles.formGroup}>
              <h3>
                <label htmlFor="note">Bemerkung</label>
              </h3>
              <textarea
                id="note"
                name="note"
                value={formValues.note}
                onChange={handleInputChange}
                className={styles.textarea}
              />
            </div>
            <div className={commonStyles.formGroup}>
              <p>
                Nach dem Absenden überprüfen wir deine Anfrage und du erhältst eine Bestätigungsemail an die angegebene E-Mailadresse.
              </p>
            </div>
          </div>

          <div className={styles.popupButtonContainer}>
            <button type="button" className={`${commonStyles.commonButton} ${commonStyles.backButton}`} onClick={handleClose}>
              Abbrechen
            </button>
            <button type="submit" disabled={isLoading} className={`${commonStyles.commonButton} ${commonStyles.submitButton}`} >

              {isLoading ? (
                <div className={commonStyles.spinner}></div>
              ) : (
                "Buchung anfragen"
              )}
            </button>
          </div>

        </form>
      </Popup>

      {isInfoOpen && (
        <Popup
          title={"Information zur Wiederholung"}
          submitLabel={""}
          closeLabel={""}
          isWithButton={false}
          isOpen={true}
          onClose={handleInfoClose}
        >
          <p>
            Wöchentliche Termine gelten für ausgewählte Woche und die nächsten drei Wochen. Schließtage sowie bereits ausgebuchte Tage werden automatisch aus der Anfrage entfernt.
          </p>

        </Popup>
      )}
      {isPriceOpen && (
        <Popup
          title={"Tarife"}
          submitLabel={""}
          closeLabel={""}
          isWithButton={false}
          isOpen={true}
          onClose={handlePriceClose}
        >
          <p>
            Die Kosten der Flexi-Buchung, die sich auf einen Block/Doppelstunde (= 120 Minuten) und pro Kind beziehen, betragen:
          </p>
          <ul>
            {costData.map((cost) => (
              <li key={cost.id}>
                für {cost.category}: {cost.price.toFixed(2)} €
              </li>
            ))}
          </ul>

        </Popup>
      )}
      <SuccessPopup isPopupOpen={isSuccessPopupOpen} onClose={() => {
        handleClose();
        setIsSuccessPopupOpen(false);
      }} />
    </>
  );
}
